import type { MenuProps } from 'antd';
import { Dropdown as AntdDropdown, Checkbox, Radio } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import type { ItemType as AntdItemType } from 'antd/es/menu/interface';
import type { RadioChangeEvent } from 'antd/es/radio';
import classNames from 'classnames';
import { type FC, type PropsWithChildren, useEffect, useRef } from 'react';
import { useHotkeyLogger } from '@/utils';
import { Button } from '../MiButton';
import ArrowDown from './icons';
import type {
    ButtonAttributes,
    CheckboxAttributes,
    DefaultAttributes,
    DividerAttributes,
    DropdownProps,
    Item,
    ItemAttributes,
    RadioAttributes,
} from './types';

const isCheckbox = (item: ItemAttributes): item is CheckboxAttributes => item.type === 'checkbox';

const isRadio = (item: ItemAttributes): item is RadioAttributes => item.type === 'radio';
const isButton = (item: ItemAttributes): item is ButtonAttributes => item.type === 'button';
const isDefault = (item: ItemAttributes): item is DefaultAttributes => item.type === undefined;

const Label: FC<ItemAttributes & { className?: string; closeDropdown?: () => void }> = ({
    className,
    closeDropdown,
    ...attr
}) => {
    const generalTextClasses = 'text-xs dark:text-font-quarternary font-normal';
    const { title, itemKey } = attr as ItemAttributes & {
        className?: string;
        itemKey: string;
        title?: string;
        onChange: (value: boolean, key: string, e: CheckboxChangeEvent) => void;
        indeterminate: boolean;
        overrideClassName?: boolean;
        disableHover?: boolean;
    };
    const handleClick = (e: React.MouseEvent) => {
        if (!isCheckbox && !isRadio) {
            closeDropdown?.();
        }
    };
    const hotkey = 'keys' in attr ? attr.keys : undefined;
    const onClick = 'onClick' in attr ? attr.onClick : undefined;
    useHotkeyLogger(hotkey, onClick);

    const PreventCloseWrapper: FC<{
        children: React.ReactNode;
        wrapperProps?: React.HTMLAttributes<HTMLDivElement>;
    }> = ({ children, wrapperProps }) => (
        <div
            {...wrapperProps}
            onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                e.nativeEvent?.stopPropagation?.();
                wrapperProps?.onClick?.(e);
            }}
            onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                wrapperProps?.onMouseDown?.(e);
            }}
        >
            {children}
        </div>
    );

    switch (true) {
        case isCheckbox(attr): {
            const { onChange, indeterminate, checked, disabled } = attr;
            return (
                <PreventCloseWrapper>
                    <Checkbox
                        className="w-full"
                        checked={checked}
                        indeterminate={indeterminate}
                        disabled={disabled}
                        onChange={(e: CheckboxChangeEvent) => {
                            onChange?.(e.target.checked, itemKey, e);
                        }}
                    >
                        <span className={classNames(className, generalTextClasses)}>{title}</span>
                    </Checkbox>
                </PreventCloseWrapper>
            );
        }

        case isRadio(attr): {
            const { onChange, value, options, radioGroupSize } = attr;
            return (
                <PreventCloseWrapper>
                    <Radio.Group
                        className={classNames(className, generalTextClasses)}
                        value={value}
                        size={radioGroupSize}
                        onChange={(e: RadioChangeEvent) => {
                            (onChange as (value: any, key: string) => void)?.(e.target.value, itemKey);
                        }}
                        name={title}
                    >
                        {options?.map((option, index) => {
                            if (typeof option === 'object' && 'label' in option) {
                                return (
                                    <Radio key={option.value} value={option.value}>
                                        {option.label}
                                    </Radio>
                                );
                            }
                            return (
                                <Radio key={index} value={option}>
                                    {option}
                                </Radio>
                            );
                        })}
                    </Radio.Group>
                </PreventCloseWrapper>
            );
        }

        case isButton(attr): {
            const { antdType, onClick: buttonOnClick, disabled } = attr;
            return (
                <PreventCloseWrapper>
                    <Button
                        className={classNames(className, generalTextClasses)}
                        type={antdType || 'default'}
                        onClick={buttonOnClick}
                        disabled={disabled}
                    >
                        {title}
                    </Button>
                </PreventCloseWrapper>
            );
        }

        case isDefault(attr): {
            const { icon, keys, onClick: defaultOnClick, onChange, overrideClassName, disableHover } = attr;
            const isTitleOnly = !icon && !keys && !defaultOnClick && !onChange;

            return (
                <PreventCloseWrapper
                    wrapperProps={{
                        className:
                            overrideClassName && isTitleOnly
                                ? `${className} dropdown-title-only`
                                : classNames(
                                      className,
                                      generalTextClasses,
                                      icon ? 'pb-0 pl-0 pt-0' : 'pl-6',
                                      'flex items-center justify-between pb-1 pt-1',
                                  ),
                        onClick: defaultOnClick,
                        style:
                            disableHover && isTitleOnly
                                ? {
                                      pointerEvents: 'none',
                                      cursor: 'default',
                                      backgroundColor: 'transparent',
                                      color: 'inherit',
                                  }
                                : undefined,
                    }}
                >
                    <div className="flex items-center">
                        {icon && (
                            <div className="flex h-6 w-6 items-center justify-center pr-2 text-slate-600">{icon}</div>
                        )}
                        {title}
                    </div>
                    {keys && (
                        <div className="ml-1 rounded-md border border-gray-400 bg-gray-300 px-[4px] text-[9px]">
                            {keys.join('+')}
                        </div>
                    )}
                </PreventCloseWrapper>
            );
        }

        default:
            return (
                <div className={classNames(className, generalTextClasses, 'cursor-pointer')} onClick={handleClick}>
                    {attr.title}
                </div>
            );
    }
};

const mapToMenuItems = (item: Item | DividerAttributes, closeDropdown?: () => void): AntdItemType => {
    if (item === null) return null;

    if (item.type === 'divider') {
        return { type: 'divider' };
    }
    if ('customElement' in item && item.customElement) {
        return {
            key: item.itemKey,
            label: item.customElement,
            className: '', // optional custom class name
            disabled: false,
        };
    }

    const { children, antProps, ...attributes } = item;
    const { itemKey } = attributes;

    const isTitleOnly =
        !('icon' in attributes) && !('keys' in attributes) && !('onClick' in attributes) && !('onChange' in attributes);

    const isOverrideClassName = 'overrideClassName' in attributes && attributes.overrideClassName;
    const shouldDisableHover = 'disableHover' in attributes && attributes.disableHover;

    const customClassName =
        isTitleOnly && isOverrideClassName && shouldDisableHover ? 'dropdown-title-only' : undefined;

    const baseItem = {
        key: itemKey,
        label: <Label {...attributes} closeDropdown={closeDropdown} />,
        className: customClassName,
        disabled: shouldDisableHover && isTitleOnly,
        ...antProps,
    };

    if (children) {
        return {
            ...baseItem,
            children: children.map(child => mapToMenuItems(child, closeDropdown)),
        };
    }

    return baseItem;
};

const DropdownWrapper: FC<PropsWithChildren<DropdownProps>> = ({
    dropdownItems,
    dropDownButtonClass,
    openKeys,
    onOpenKeys,
    onOpenChange,
    open,
    children,
    ...props
}) => {
    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const handleOpenChange = (openState: boolean, info: { source: 'menu' | 'trigger' }) => {
        onOpenChange?.(openState, info);
    };

    const closeDropdown = () => onOpenChange?.(false, { source: 'trigger' });

    const items: MenuProps['items'] = dropdownItems.map(item => mapToMenuItems(item, closeDropdown));

    return (
        <AntdDropdown
            menu={{
                items,
                style: { borderRadius: '4px', border: '1px #DDD solid' },
                openKeys,
                onOpenChange: onOpenKeys,
            }}
            open={open}
            onOpenChange={handleOpenChange}
            trigger={['contextMenu']}
            {...props}
        >
            {children ?? (
                <button
                    className={`group flex cursor-pointer items-center justify-center rounded p-1 hover:bg-blue-500 focus:outline-none dark:hover:bg-select ${
                        dropDownButtonClass ?? ''
                    }`}
                >
                    <ArrowDown className="fill-current text-slate-900 group-hover:text-black dark:text-fill-secondary dark:group-hover:text-font-tertiary" />
                </button>
            )}
        </AntdDropdown>
    );
};
export default DropdownWrapper;
